/**
 * 等级 badges, LV1 to LV100 — 极简字标: no plate, LV small on the level's
 * foot, the level large in its band's gradient, a short rule under it, in
 * the letters the VIP badges are drawn in (badgeGlyphs). The colour moves
 * on every ten levels, cool to warm to gold — grey, green, teal, blue,
 * indigo, violet, magenta, orange, red, gold — and LV100 is the spectrum
 * with a spark; the dark theme takes each band's lighter end. Shared
 * byte-identical by both consoles.
 */
import { letters, measure, word } from '@/utils/badgeGlyphs'

export const LV_MAX = 100

// The levels a ladder shows: where each colour starts, and the top.
export const LV_MILESTONES = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

// Each band light, mid and deep; LV100's spectrum, and the violets its LV
// is written in (dark theme, light theme).
const RAMP = [
  ['#D5DAE1', '#A3ACB9', '#7B8594'],
  ['#8DE3B0', '#34C07B', '#1A965A'],
  ['#7EE3DD', '#19B2AB', '#0C8883'],
  ['#A0CAFF', '#3E8CF5', '#1E61D3'],
  ['#B3B6FF', '#5D62EE', '#3D40C5'],
  ['#D4B4FF', '#9459EF', '#6B35C8'],
  ['#F8B5E4', '#D34EB5', '#A53189'],
  ['#FFC995', '#F27B2E', '#C85512'],
  ['#FFA9A3', '#E84440', '#BB2525'],
  ['#FFE394', '#E9A617', '#B47705'],
]
const SPECTRUM = ['#FF5C8E', '#FFA63B', '#45D49A', '#3CA8F5', '#9A69F7']
const TOP_INK = ['#C9A8FF', '#B48BFF']

const clampLV = (lv: number) => Math.min(LV_MAX, Math.max(1, Math.floor(lv || 1)))

/** The colour of level lv: its band's, LV100 the middle of the spectrum. */
export function lvColor(lv: number): string {
  const n = clampLV(lv)
  return n >= LV_MAX ? SPECTRUM[3] : RAMP[Math.floor(n / 10)][1]
}

/** The colour of level lv's progress bar: its own, LV100's the spectrum (as a-progress takes it). */
export function lvBarColor(lv: number): string | Record<string, string> {
  if (clampLV(lv) < LV_MAX) return lvColor(lv)
  return Object.fromEntries(SPECTRUM.map((c, i) => [`${Math.round((i / (SPECTRUM.length - 1)) * 100)}%`, c]))
}

// LV 5.6 high on the level's foot, the level 10 high, the rule under the
// level. The gap between them is wider than the letters' own: the italic
// V's arm leans toward the level, and at 1.2 the two all but touched.
const H = 16
const HL = 5.6
const HN = 10
const GAP = 3.4
const TOP = 1.4

/**
 * The badge for level lv at scale times its size (16px high). prefix keeps
 * its gradient ids apart from every other badge on the page.
 *
 * The colours that change with the theme are CSS variables: --lvb-ink (LV
 * and the rule), --lvb-s0 and --lvb-s1 (the level's gradient) fall back to
 * the light theme's, and the badge carries the dark theme's as --lvb-*-dark
 * for LvBadge's dark rule.
 */
export function lvBadgeSvg(lv: number, scale = 1, prefix = 'lvb'): string {
  const n = clampLV(lv)
  const top = n >= LV_MAX
  const label = String(n)
  const wL = measure('LV', HL)
  const wN = measure(label, HN)
  const xN = wL + GAP
  const W = xN + wN + 0.6
  const [light, mid, deep] = top ? ['', '', ''] : RAMP[Math.floor(n / 10)]
  const ink = top ? TOP_INK[1] : mid
  const inkDark = top ? TOP_INK[0] : light
  // The level's gradient runs across its letters in their own grid units.
  const id = `${prefix}-wm`
  const across = `gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${word(label).w}" y2="8"`
  const stops = top
    ? SPECTRUM.map((c, i) => `<stop offset="${(i / (SPECTRUM.length - 1)).toFixed(2)}" stop-color="${c}"/>`).join('')
    : `<stop offset="0" style="stop-color:var(--lvb-s0, ${mid})"/><stop offset="1" style="stop-color:var(--lvb-s1, ${deep})"/>`
  const vars = top ? `--lvb-ink-dark:${inkDark}` : `--lvb-ink-dark:${inkDark};--lvb-s0-dark:${light};--lvb-s1-dark:${mid}`
  const body =
    `<rect x="${(xN + 0.4).toFixed(2)}" y="13.4" width="${(wN - 0.4).toFixed(2)}" height="1.6" rx=".8" style="fill:var(--lvb-ink, ${ink})" opacity=".32"/>` +
    letters('LV', 0, TOP + HN - HL, HL, `var(--lvb-ink, ${ink})`, 2.3) +
    letters(label, xN, TOP, HN, `url(#${id})`, 2) +
    (top ? `<path d="M${(W - 0.6).toFixed(2)} -1.8l.68 1.92 1.92.68-1.92.68-.68 1.92-.68-1.92-1.92-.68 1.92-.68z" fill="#FFC94D"/>` : '')
  return `<svg width="${(W * scale).toFixed(1)}" height="${(H * scale).toFixed(1)}" viewBox="0 0 ${W.toFixed(2)} ${H}" style="display:block;overflow:visible;${vars}" aria-hidden="true"><defs><linearGradient id="${id}" ${across}>${stops}</linearGradient></defs>${body}</svg>`
}

/** A level as the server works it out from 经验 (service.ExpLevel). */
export interface ExpLevel {
  lv: number
  exp: number
  /** The 经验 the level starts at, and the next one does (0 at the top). */
  floor: number
  next: number
}

/**
 * Where a level's 经验 stands: what it has of the next level, out of what
 * that takes, and the share; the top level is full.
 */
export function lvProgress(l: ExpLevel): { cur: number; need: number; percent: number; top: boolean } {
  const cur = Math.max(0, l.exp - l.floor)
  if (!l.next || l.lv >= LV_MAX) return { cur, need: 0, percent: 1, top: true }
  const need = l.next - l.floor
  return { cur, need, percent: need > 0 ? Math.min(1, cur / need) : 0, top: false }
}
